import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, ToggleLeft, ToggleRight, Info } from 'lucide-react';
import { masterSettingsAPI } from '../../api/api';

const AttendanceFine = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    minimum_percentage: 75,
    fine_amount: 500,
    apply_to: 'all', // 'all' or 'specific'
    course_id: '',
    calculation_period: 'monthly' // 'monthly' or 'semester'
  });
  const [courses, setCourses] = useState([]);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [existingId, setExistingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch existing attendance fine settings
      const [finesRes, coursesRes] = await Promise.all([
        masterSettingsAPI.getAttendanceFines(),
        masterSettingsAPI.getCourses()
      ]);
      
      setCourses(coursesRes.data || []);
      
      // Check if there's an existing simplified setting
      if (finesRes.data && finesRes.data.length > 0) {
        const existing = finesRes.data[0];
        setHasExistingSettings(true);
        setExistingId(existing.id);
        setSettings({
          enabled: true,
          minimum_percentage: existing.minimum_percentage || 75,
          fine_amount: existing.fine_amount || 500,
          apply_to: existing.course_id ? 'specific' : 'all',
          course_id: existing.course_id || '',
          calculation_period: existing.calculation_period || 'monthly'
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: 'Attendance Fine Rule',
        minimum_percentage: settings.minimum_percentage,
        fine_amount: settings.fine_amount,
        course_id: settings.apply_to === 'specific' ? settings.course_id : null,
        calculation_period: settings.calculation_period,
        description: `Fine of ₹${settings.fine_amount} for attendance below ${settings.minimum_percentage}%`
      };

      if (settings.enabled) {
        if (hasExistingSettings && existingId) {
          await masterSettingsAPI.updateAttendanceFine(existingId, payload);
        } else {
          const response = await masterSettingsAPI.createAttendanceFine(payload);
          setExistingId(response.data.id);
          setHasExistingSettings(true);
        }
        alert('✅ Attendance Penalty settings saved successfully!');
      } else {
        // If disabled and exists, delete it
        if (hasExistingSettings && existingId) {
          await masterSettingsAPI.deleteAttendanceFine(existingId);
          setHasExistingSettings(false);
          setExistingId(null);
        }
        alert('✅ Attendance Penalty has been disabled.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Attendance Penalty Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Configure automatic penalty for low attendance</p>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">How it works:</p>
          <p className="mt-1">Students with attendance below the minimum percentage will be automatically penalized. The penalty will appear in their fee dues.</p>
        </div>
      </div>

      {/* Main Settings Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Enable/Disable Toggle */}
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Enable Attendance Penalty</h2>
            <p className="text-sm text-gray-500">Turn on to automatically apply penalties</p>
          </div>
          <button
            onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
            className={`p-1 rounded-full transition-colors ${settings.enabled ? 'text-green-600' : 'text-gray-400'}`}
          >
            {settings.enabled ? (
              <ToggleRight size={40} strokeWidth={1.5} />
            ) : (
              <ToggleLeft size={40} strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Settings Form */}
        <div className={`p-6 space-y-6 ${!settings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Minimum Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Attendance Required
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="50"
                max="95"
                step="5"
                value={settings.minimum_percentage}
                onChange={(e) => setSettings({ ...settings, minimum_percentage: parseInt(e.target.value) })}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="w-20 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <span className="text-lg font-bold text-blue-700">{settings.minimum_percentage}%</span>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Students with attendance below {settings.minimum_percentage}% will be penalized
            </p>
          </div>

          {/* Fine Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Penalty Amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
              <input
                type="number"
                min="0"
                step="100"
                value={settings.fine_amount}
                onChange={(e) => setSettings({ ...settings, fine_amount: parseInt(e.target.value) || 0 })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="500"
              />
            </div>
          </div>

          {/* Calculation Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Calculate Penalty Per
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSettings({ ...settings, calculation_period: 'monthly' })}
                className={`px-4 py-3 rounded-lg border-2 text-center transition-all ${
                  settings.calculation_period === 'monthly'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">Monthly</span>
                <p className="text-xs mt-1 opacity-75">Penalty applied each month</p>
              </button>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, calculation_period: 'semester' })}
                className={`px-4 py-3 rounded-lg border-2 text-center transition-all ${
                  settings.calculation_period === 'semester'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">Semester</span>
                <p className="text-xs mt-1 opacity-75">Penalty applied per semester</p>
              </button>
            </div>
          </div>

          {/* Apply To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apply To
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="apply_to"
                  checked={settings.apply_to === 'all'}
                  onChange={() => setSettings({ ...settings, apply_to: 'all', course_id: '' })}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <span className="font-medium text-gray-900">All Students</span>
                  <p className="text-sm text-gray-500">Apply penalty to all courses</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="apply_to"
                  checked={settings.apply_to === 'specific'}
                  onChange={() => setSettings({ ...settings, apply_to: 'specific' })}
                  className="w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Specific Course</span>
                  <p className="text-sm text-gray-500">Apply penalty to selected course only</p>
                </div>
              </label>
              
              {settings.apply_to === 'specific' && (
                <select
                  value={settings.course_id}
                  onChange={(e) => setSettings({ ...settings, course_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg ml-7"
                >
                  <option value="">Select Course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Summary & Save */}
        <div className="px-6 py-4 bg-gray-50 border-t">
          {settings.enabled && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">Summary:</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                Students with attendance below <strong>{settings.minimum_percentage}%</strong> will be fined <strong>₹{settings.fine_amount}</strong> per <strong>{settings.calculation_period}</strong>
                {settings.apply_to === 'specific' && settings.course_id && (
                  <> for <strong>{courses.find(c => c.id === settings.course_id)?.name || 'selected course'}</strong></>
                )}
              </p>
            </div>
          )}
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceFine;
